import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';

const SidebarContext = createContext();

const LS_COLLAPSED = 'archon.sidebar.collapsed';
const LS_HIDDEN = 'archon.sidebar.hidden';

/** Progressive sidebar: expanded → collapsed → hidden (hide requires collapsed first). */
export const SIDEBAR_MODE = {
  EXPANDED: 'expanded',
  COLLAPSED: 'collapsed',
  HIDDEN: 'hidden',
};

function readStoredBool(key, defaultValue) {
  try {
    if (typeof window === 'undefined') return defaultValue;
    const v = window.localStorage.getItem(key);
    if (v === null) return defaultValue;
    return v === '1';
  } catch {
    return defaultValue;
  }
}

function writeStoredBool(key, value) {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(key, value ? '1' : '0');
  } catch {
    /* ignore quota / private mode */
  }
}

export function deriveSidebarMode(collapsed, hidden) {
  if (hidden) return SIDEBAR_MODE.HIDDEN;
  if (collapsed) return SIDEBAR_MODE.COLLAPSED;
  return SIDEBAR_MODE.EXPANDED;
}

/** Paths where we do not auto-collapse on navigation (hub screens). */
export const SIDEBAR_AUTO_COLLAPSE_SKIP_PATHS = new Set(['/projects']);

export function shouldSkipAutoCollapseForPath(pathname) {
  return SIDEBAR_AUTO_COLLAPSE_SKIP_PATHS.has(pathname);
}

/** Effective sidebar width in px for layout (desktop). */
export function getSidebarWidthPx(collapsed, hidden) {
  if (hidden) return 0;
  return collapsed ? 60 : 234;
}

/**
 * Horizontal space to reserve for the fixed “show nav” control when the sidebar
 * is fully hidden (desktop). Matches NavigationBar: left 10px + sm button + gap.
 */
export const SIDEBAR_FLOATING_TOGGLE_RESERVE_PX = 56;

export function getSidebarMinimizeLabel(mode) {
  if (mode === SIDEBAR_MODE.EXPANDED) return 'Collapse sidebar';
  if (mode === SIDEBAR_MODE.COLLAPSED) return 'Hide sidebar';
  return 'Show sidebar';
}

function SidebarEffectsBridge() {
  const location = useLocation();
  const { user } = useAuth();
  const { minimizeSidebar, revealSidebar, expandSidebar, sidebarMode } = useSidebar();

  const pathPrevRef = React.useRef(null);

  useEffect(() => {
    if (!user) return;
    const p = location.pathname;
    if (pathPrevRef.current === null) {
      pathPrevRef.current = p;
      return;
    }
    if (pathPrevRef.current === p) return;
    pathPrevRef.current = p;
    if (shouldSkipAutoCollapseForPath(p)) return;
    if (sidebarMode === SIDEBAR_MODE.EXPANDED) {
      minimizeSidebar();
    }
  }, [location.pathname, user, minimizeSidebar, sidebarMode]);

  useEffect(() => {
    if (!user) return undefined;

    const onKeyDown = (e) => {
      if (e.defaultPrevented) return;
      const t = e.target;
      if (
        t &&
        (t.tagName === 'INPUT' ||
          t.tagName === 'TEXTAREA' ||
          t.tagName === 'SELECT' ||
          (typeof t.isContentEditable === 'boolean' && t.isContentEditable))
      ) {
        return;
      }

      const mod = e.ctrlKey || e.metaKey;
      const key = typeof e.key === 'string' ? e.key.toLowerCase() : '';

      if (mod && e.shiftKey && key === 'b') {
        e.preventDefault();
        if (sidebarMode === SIDEBAR_MODE.COLLAPSED) {
          minimizeSidebar();
        } else if (sidebarMode === SIDEBAR_MODE.EXPANDED) {
          minimizeSidebar();
        }
        return;
      }

      if (mod && !e.shiftKey && key === 'b') {
        e.preventDefault();
        if (sidebarMode === SIDEBAR_MODE.HIDDEN) {
          revealSidebar();
        } else if (sidebarMode === SIDEBAR_MODE.COLLAPSED) {
          expandSidebar();
        } else {
          minimizeSidebar();
        }
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [user, sidebarMode, minimizeSidebar, revealSidebar, expandSidebar]);

  return null;
}

export const SidebarProvider = ({ children }) => {
  const [collapsed, setCollapsedState] = useState(() => readStoredBool(LS_COLLAPSED, false));
  const [sidebarHidden, setSidebarHiddenState] = useState(() => readStoredBool(LS_HIDDEN, false));

  useEffect(() => {
    writeStoredBool(LS_COLLAPSED, collapsed);
  }, [collapsed]);

  useEffect(() => {
    writeStoredBool(LS_HIDDEN, sidebarHidden);
  }, [sidebarHidden]);

  useEffect(() => {
    if (sidebarHidden && !collapsed) {
      setCollapsedState(true);
    }
  }, [sidebarHidden, collapsed]);

  const sidebarMode = deriveSidebarMode(collapsed, sidebarHidden);

  const setCollapsed = useCallback((next) => {
    setCollapsedState((prev) => {
      const value = typeof next === 'function' ? next(prev) : next;
      if (sidebarHidden && value === false) {
        return prev;
      }
      return value;
    });
  }, [sidebarHidden]);

  const setSidebarHidden = useCallback((next) => {
    setSidebarHiddenState((prev) => {
      const value = typeof next === 'function' ? next(prev) : next;
      if (value && !collapsed) {
        return prev;
      }
      return value;
    });
  }, [collapsed]);

  const minimizeSidebar = useCallback(() => {
    if (sidebarHidden) return;
    if (!collapsed) {
      setCollapsedState(true);
      return;
    }
    setSidebarHiddenState(true);
  }, [collapsed, sidebarHidden]);

  const revealSidebar = useCallback(() => {
    setSidebarHiddenState(false);
    setCollapsedState(true);
  }, []);

  const expandSidebar = useCallback(() => {
    if (sidebarHidden) return;
    setCollapsedState(false);
  }, [sidebarHidden]);

  const toggleSidebarExpanded = useCallback(() => {
    if (sidebarHidden) return;
    setCollapsedState((prev) => !prev);
  }, [sidebarHidden]);

  const value = useMemo(
    () => ({
      collapsed,
      setCollapsed,
      sidebarHidden,
      setSidebarHidden,
      sidebarMode,
      minimizeSidebar,
      revealSidebar,
      expandSidebar,
      toggleSidebarExpanded,
      effectiveSidebarWidthPx: getSidebarWidthPx(collapsed, sidebarHidden),
    }),
    [
      collapsed,
      setCollapsed,
      sidebarHidden,
      setSidebarHidden,
      sidebarMode,
      minimizeSidebar,
      revealSidebar,
      expandSidebar,
      toggleSidebarExpanded,
    ],
  );

  return (
    <SidebarContext.Provider value={value}>
      <SidebarEffectsBridge />
      {children}
    </SidebarContext.Provider>
  );
};

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};
